import { isAgentsRoute } from "constants/agentLaunchables";
import {
  Bot,
  BookOpen,
  CreditCard,
  KeyRound,
  Rocket,
  Server,
  Settings,
  Users,
} from "lucide-react";
import {
  buildOrganizationSettingsTabHref,
  getOrganizationSettingsTabFromSearch,
} from "components/OrgSettings/settingsTabs";

import type { ShellNavItem, ShellNavSection } from "./types";

export type ApplicationNavigationItemId =
  | "environments"
  | "agents"
  | "launchables"
  | "secrets"
  | "team"
  | "billing"
  | "settings";

interface BuildApplicationNavigationOptions {
  activeOrgId: string;
  includeSecrets: boolean;
  includeTeam: boolean;
}

const orgPath = (activeOrgId: string, page: string) =>
  activeOrgId ? `/org/${activeOrgId}/${page}` : `/org/${page}`;

export const buildApplicationNavigation = ({
  activeOrgId,
  includeSecrets,
  includeTeam,
}: BuildApplicationNavigationOptions): {
  primarySections: ShellNavSection[];
  primaryItems: ShellNavItem[];
  footerItems: ShellNavItem[];
} => {
  const primarySections: ShellNavSection[] = [
    {
      id: "build",
      label: "Build",
      items: [
        {
          id: "environments",
          label: "Compute",
          href: orgPath(activeOrgId, "environments"),
          icon: Server,
        },
        {
          id: "agents",
          label: "Agents",
          href: "/agents",
          icon: Bot,
        },
        {
          id: "launchables",
          label: "Launchables",
          href: orgPath(activeOrgId, "launchables"),
          icon: Rocket,
        },
      ],
    },
    {
      id: "organization",
      label: "Organization",
      items: [
        {
          id: "settings",
          label: "Settings",
          href: buildOrganizationSettingsTabHref(activeOrgId),
          icon: Settings,
        },
        ...(includeTeam
          ? [
              {
                id: "team",
                label: "Team",
                href: buildOrganizationSettingsTabHref(activeOrgId, "team"),
                icon: Users,
              } satisfies ShellNavItem,
            ]
          : []),
        ...(includeSecrets
          ? [
              {
                id: "secrets",
                label: "Secrets",
                href: buildOrganizationSettingsTabHref(activeOrgId, "secrets"),
                icon: KeyRound,
                badge: "New",
              } satisfies ShellNavItem,
            ]
          : []),
        {
          id: "billing",
          label: "Billing",
          href: buildOrganizationSettingsTabHref(activeOrgId, "billing"),
          icon: CreditCard,
        },
      ],
    },
    {
      id: "learn",
      label: "Learn",
      items: [
        {
          id: "documentation",
          label: "Documentation",
          href: "https://docs.nvidia.com/brev/latest",
          icon: BookOpen,
          external: true,
        },
      ],
    },
  ];
  const primaryItems = primarySections.flatMap((section) => section.items);

  return {
    primarySections,
    primaryItems,
    footerItems: [],
  };
};

export const getActiveApplicationNavigationItemId = (
  pathname: string,
  search = "",
): ApplicationNavigationItemId | "" => {
  if (isAgentsRoute(pathname, search)) return "agents";

  if (
    pathname === "/environment/new" ||
    pathname === "/environments/new" ||
    pathname === "/org/environments" ||
    /^\/org\/[^/]+\/(?:environments|nodes)(?:\/|$)/.test(pathname)
  ) {
    return "environments";
  }

  if (
    pathname.startsWith("/launchables") ||
    pathname.startsWith("/launchable/") ||
    pathname === "/org/launchables" ||
    /^\/org\/[^/]+\/launchables(?:\/|$)/.test(pathname)
  ) {
    return "launchables";
  }

  if (
    pathname === "/org/secrets" ||
    /^\/org\/[^/]+\/secrets(?:\/|$)/.test(pathname)
  ) {
    return "secrets";
  }

  if (
    pathname === "/org/team" ||
    /^\/org\/[^/]+\/team(?:\/|$)/.test(pathname)
  ) {
    return "team";
  }

  if (
    pathname === "/org/billing" ||
    /^\/org\/[^/]+\/billing(?:\/|$)/.test(pathname)
  ) {
    return "billing";
  }

  if (
    pathname === "/org/settings" ||
    /^\/org\/[^/]+\/settings(?:\/|$)/.test(pathname)
  ) {
    const tab = getOrganizationSettingsTabFromSearch(search);
    if (tab !== "settings") return tab;
    return "settings";
  }

  return "";
};

interface BrowserLocationParts {
  pathname: string;
  search?: string;
  hash?: string;
}

/**
 * Updates an organization-scoped route while preserving query and hash state.
 * Global routes such as /agents remain global when the organization changes.
 */
export const getOrganizationSwitchHref = (
  location: BrowserLocationParts,
  nextOrganizationId: string,
): string | null => {
  const nextPathname = location.pathname.replace(
    /^\/org\/[^/]+(?=\/|$)/,
    `/org/${nextOrganizationId}`,
  );

  if (nextPathname === location.pathname) return null;

  return `${nextPathname}${location.search ?? ""}${location.hash ?? ""}`;
};
